// components/source/sourceAgentContext.ts
// Builds the Master's SOURCE awareness block: a name->id directory of the portfolios
// and properties (the top-level sourcing scopes the user names, e.g. "the Altarea
// shortlist"), plus any sourcing campaigns already loaded. A Master session has no
// activeSubject, so these ids are the addressing scheme, exactly like Studio passes an
// explicit projectId: the Master maps what the user says to a portfolioId / propertyId
// / campaignId and passes it to SOURCE_SHOW_IN_CHAT (which queries the shortlist fits
// directly by that id). PURE read from the kernel inventory (the master turn pulls the
// campaigns + portfolios in first); never mutates, never navigates.
import type { KernelHandle } from '#types/types';

const str = (v: unknown): string => (typeof v === 'string' ? v : v == null ? '' : String(v));
const firstStr = (r: Record<string, unknown>, keys: string[]): string => {
  for (const k of keys) {
    const s = str(r[k]).trim();
    if (s) return s;
  }
  return '';
};
const firstNum = (r: Record<string, unknown>, keys: string[]): string => {
  for (const k of keys) {
    const v = r[k];
    if (typeof v === 'number' && Number.isFinite(v)) return String(v);
  }
  return '';
};
const nameFromReg = (reg: Record<string, unknown> | undefined, id: unknown): string => {
  const key = str(id);
  if (!reg || !key) return '';
  const e = reg[key];
  return e && typeof e === 'object' ? firstStr(e as Record<string, unknown>, ['name', 'displayName']) : '';
};

const MAX_PER_SECTION = 40;

// List a registry's entities as name-id lines, capped. Returns [lines, truncatedCount].
function listEntities(
  reg: Record<string, unknown> | undefined,
  idLabel: string,
  nameKeys: string[]
): [string[], number] {
  if (!reg) return [[], 0];
  const ids = Object.keys(reg);
  const lines: string[] = [];
  for (const id of ids) {
    if (lines.length >= MAX_PER_SECTION) break;
    const e = reg[id];
    if (!e || typeof e !== 'object') continue;
    const name = firstStr(e as Record<string, unknown>, nameKeys) || '(unnamed)';
    lines.push(`- "${name}" ${idLabel}=${id}`);
  }
  return [lines, Math.max(0, ids.length - lines.length)];
}

export function buildSourceContext(k: KernelHandle | null | undefined): string | null {
  if (!k) return null;
  let inv: ReturnType<KernelHandle['getInventory']>;
  try {
    inv = k.getInventory();
  } catch {
    return null;
  }
  if (!inv) return null;

  const propReg = inv.Property?.registry as Record<string, unknown> | undefined;
  const portReg = inv.Portfolio?.registry as Record<string, unknown> | undefined;

  const [portLines, portMore] = listEntities(portReg, 'portfolioId', ['name', 'displayName']);
  const [propLines, propMore] = listEntities(propReg, 'propertyId', ['name', 'displayName']);

  // Campaigns already loaded (optional: a direct campaignId is another way in). ONE registry at
  // every granularity: `targetKind` says what a run was about, so the scope is read off the row
  // instead of being inferred from which of three registries it came out of.
  const campReg = inv.Campaign?.registry as Record<string, unknown> | undefined;
  const campLines: string[] = [];
  for (const id of Object.keys(campReg ?? {})) {
    if (campLines.length >= MAX_PER_SECTION) break;
    const c = campReg?.[id];
    if (!c || typeof c !== 'object') continue;
    const r = c as Record<string, unknown>;
    const isPortfolioWide = str(r.targetKind).toUpperCase() === 'PORTFOLIO';
    const scope = isPortfolioWide ? 'portfolio' : str(r.targetKind).toUpperCase() === 'UNIT' ? 'unit' : 'asset';
    // The run's own name if it was given one at launch, else what it targets, else the target
    // entity's name from the directory above.
    const cfg = r.config;
    const name =
      (cfg && typeof cfg === 'object' ? firstStr(cfg as Record<string, unknown>, ['name']) : '') ||
      firstStr(r, ['targetName']) ||
      (isPortfolioWide
        ? nameFromReg(portReg, r.portfolioId)
        : nameFromReg(propReg, Array.isArray(r.propertyIds) ? r.propertyIds[0] : undefined)) ||
      '(unnamed)';
    const status = str(r.status) || 'n/a';
    // The list route already reduced leads / portfolio fits / unit fits to this run's own result
    // family, excluding rows a human took off the list.
    const count = firstNum(r, ['resultCount', 'totalResultCount']);
    campLines.push(`- [${scope}] "${name}" campaignId=${id} status=${status}${count ? ` fits=${count}` : ''}`);
  }

  const framing =
    'SOURCE CONTEXT (server-side inventory). Source data (a portfolio or property\'s ' +
    'shortlist of scored brand fits, brand contacts, brand fiches) is NOT on screen: the ' +
    'user sees it only when you surface it with SOURCE_SHOW_IN_CHAT. To show a shortlist, ' +
    'MATCH what the user names to a portfolio or property below and pass its id: ' +
    'SOURCE_SHOW_IN_CHAT kind=shortlist scope=portfolio portfolioId=<id> (or scope=asset ' +
    'propertyId=<id>). You do not need a campaignId; the portfolioId/propertyId is enough.';

  const sections: string[] = [framing];
  if (portLines.length > 0) {
    sections.push(
      `\nPortfolios${portMore ? ` (first ${portLines.length}, ${portMore} more)` : ''}:\n` + portLines.join('\n')
    );
  }
  if (propLines.length > 0) {
    sections.push(
      `\nProperties${propMore ? ` (first ${propLines.length}, ${propMore} more)` : ''}:\n` + propLines.join('\n')
    );
  }
  if (campLines.length > 0) {
    sections.push('\nCampaigns already loaded (optional direct campaignId):\n' + campLines.join('\n'));
  }
  if (portLines.length === 0 && propLines.length === 0 && campLines.length === 0) {
    sections.push(
      '\nNothing is loaded yet. If the user names a portfolio, property, or brand, ask them ' +
        'to open it in Source (or give its id) so it can be resolved.'
    );
  }

  return sections.join('\n');
}
